import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import type { DataTableType } from '@components/DataTable/DataTable';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import ViewTab from '@components/ViewTab';

import { useGetImagingDiagnosticReports } from '@hooks/api/diagnostic-report';

import { Spacer } from '@layouts/Layouts/BigTitleLayout';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';

import ImagingTestCardView from './ImagingTestCardView';
import ImagingTestListView from './ImagingTestListView';

const messages = defineMessages({
  title: {
    id: 'imagingTests.title',
    defaultMessage: 'Imaging & Tests',
  },
  listView: {
    id: 'imagingTests.listView',
    defaultMessage: 'List View',
  },
  cardView: {
    id: 'imagingTests.cardView',
    defaultMessage: 'Card View',
  },
});

const ImagingTestsMain: React.FC = () => {
  const { formatMessage } = useIntl();
  const { data, loading } = useGetImagingDiagnosticReports();
  const [displayData, setDisplayData] =
    useState<DiagnosticReportRecord[]>(data);

  const tabs = [
    { id: 'card-view', value: formatMessage(messages.cardView) },
    { id: 'list-view', value: formatMessage(messages.listView) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const tableData = useMemo(
    () =>
      data.map((a) => {
        return {
          id: a.id,
          viewedStatus: a.viewedStatus,
          testType: a.typeName,
          testName: a.testName,
          hasNotes: Boolean(a.recordNotes.length),
          facilityName: a.facilityName,
          date:
            activeTab.id === 'card-view' ? a.getTestDate() : a.getTestDateStr(),
          hcpName: a.practitioner,
          hasImages: a.hasImages,
          selfReported: a.selfReported,
          curationStatus: a.curationStatus,
          imagesAvailable: a.imagesAvailable,
          recordSource: a.recordSource,
        };
      }),
    [activeTab.id, data]
  );

  const filterData = useCallback(
    (filteredItems: Record<string, DataTableType>[]) => {
      const fc = filteredItems.map((a) => {
        return a.id;
      });
      setDisplayData(
        data.filter((i) => {
          return fc.find((k) => k === i.id) !== undefined;
        })
      );
    },
    [data]
  );

  useEffect(() => {
    setDisplayData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <ContentLoader loading={loading}>
      <Spacer />
      <ViewTab changeTab={setActiveTab} selected={activeTab} tabArray={tabs} />
      {activeTab.id === 'card-view' ? (
        <ImagingTestCardView
          data={data}
          displayData={displayData}
          onChange={filterData}
          tableData={tableData}
        />
      ) : null}
      {activeTab.id === 'list-view' ? (
        <ImagingTestListView tableData={tableData} />
      ) : null}
    </ContentLoader>
  );
};

export default withErrorBoundary(ImagingTestsMain, {
  FallbackComponent: ErrorBoundaryFallback,
});
